import { useCookies } from 'react-cookie'
import { useEffect, useRef, useState } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import Head from 'next/head'

import environment, { imageDirectory } from 'environment'

import { DeckSearchTypeV3, DeckSearchV3HumanName, generateDefaultDeckSearchV3 } from 'types/search'
import { HUMAN_READABLE_FORMAT } from 'types/deck'

import { useActionless, useAppSelector } from 'redux/hooks'
import { ReduxServerProps, store } from 'redux/store'
import { SET_FOLDER_STATE } from 'redux/folders/actions/types'
import { FoldersState, generateInitialFolderState } from 'redux/folders/initialState'

import CookieService, { expirationDurations } from 'services/cookie.service'
import { FolderService } from 'services/folder.service'

import DragDropProvider from 'components/misc/DragDropProvider'
import PageWrapper from 'components/misc/PageWrapper'
import CardBanner from 'components/misc/CardBanner'
import FolderBreadcrumbs from 'components/folderPage/FolderBreadcrumbs'
import PhatInput from 'components/formElements/PhatInput'
import PhatDropdown from 'components/formElements/PhatDropdown'
import FolderTable from 'components/folderPage/FolderTable'
import FolderGrid from 'components/folderPage/FolderGrid'
import FolderDragLayer from 'components/folderPage/FolderDragLayer'
import MultiSelectControls from 'components/folderPage/MultiSelectedControls'
import NewFolderModal from 'components/folderPage/NewFolderModal'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import FolderColumnControls from 'components/folderPage/FolderTable/FolderColumnControls'
import FolderFilterOverlay from 'components/folderPage/FolderFildersOverlay'
import Icon from 'components/elements/Icon'

import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'
import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './folderId.module.scss'

const ORDER_BY_COOKIE_KEY = 'folderOrderBy'
const BANNER_IMAGE = imageDirectory + 'ori/70afb856-6697-411b-b1aa-e9290a0bfd68_small.jpg'
const FOLDER_VIEW_AS = 'tbFolderViewAs'

const reduceArrayToMapById = (acc: Record<number, boolean>, item: { id: number }) => {
  acc[item.id] = true

  return acc
}

const FolderPage = () => {
  const keylistener = useRef<(e: KeyboardEvent) => void>(() => null)

  const id = useAppSelector(state => state.folders.rootFolder.id)
  const name = useAppSelector(state => state.folders.rootFolder.name)
  const privateFolder = useAppSelector(state => state.folders.rootFolder.private)
  const owner = useAppSelector(state => state.folders.rootFolder.owner)
  const subfolders = useAppSelector(state => state.folders.rootFolder.subfolders)
  const decks = useAppSelector(state => state.folders.rootFolder.decks)
  const totalResults = useAppSelector(state => state.folders.rootFolder.count)
  const nextPage = useAppSelector(state => state.folders.rootFolder.next)

  const [setFolderState] = useActionless(SET_FOLDER_STATE)

  const router = useRouter()

  const [exactPageNumber, setExactPageNumber] = useState('')
  const [searchedName, setSearchedName] = useState(`${router.query.name || ''}`)

  const [{ tbId: userId, [FOLDER_VIEW_AS]: viewAs = 'grid', [ORDER_BY_COOKIE_KEY]: cachedOrderBy }, setCookies] =
    useCookies(['tbId', FOLDER_VIEW_AS, ORDER_BY_COOKIE_KEY])

  const isFolderOwner = `${userId}` === `${owner.id}` // NOTE - We're not using the hook here because we require other cookies, and you can't call useCookies twice
  const page = Number(router.query.page || 1) || 1
  const orderBy = `${cachedOrderBy || '-updatedAt'}`.replace('-', '')
  const orderByDirDescending = `${cachedOrderBy || '-updatedAt'}`.startsWith('-')

  const [checkboxDescending, setCheckboxDescending] = useState(orderByDirDescending)

  const [searchedDeckState] = useState<DeckSearchTypeV3>(
    generateDefaultDeckSearchV3(router.query, router.pathname, cachedOrderBy),
  )

  const handleSetOrderBy = (setOrderBy: string) => {
    const newOrderBy = `${checkboxDescending ? '-' : ''}${setOrderBy}`

    router.push(`/folders/${id}?orderBy=${newOrderBy}`)
  }

  const handleSelectAll = () => {
    const payload: Partial<FoldersState> = {
      selectedDecks: decks.reduce(reduceArrayToMapById, {}),
      selectedSubfolders: subfolders.reduce(reduceArrayToMapById, {}),
    }

    setFolderState(payload)
  }

  const handleFolderNameSearch = (e: React.FormEvent) => {
    e.preventDefault()

    router.push(`/folders/${id}/?name=${searchedName}`)
  }

  useEffect(() => {
    window.removeEventListener('keydown', keylistener.current)

    keylistener.current = (e: KeyboardEvent) => {
      const controlKey = environment.getOnMac() ? e.metaKey : e.ctrlKey

      if (basicKeybindSkips(e)) return

      const folderState = store.getState().folders

      if ((e.key === 'a' || e.key === 'A') && controlKey) {
        e.preventDefault()

        const { decks, subfolders } = folderState.rootFolder

        const payload: Partial<FoldersState> = {
          selectedDecks: decks.reduce(reduceArrayToMapById, {}),
          selectedSubfolders: subfolders.reduce(reduceArrayToMapById, {}),
        }

        store.dispatch({ type: SET_FOLDER_STATE, payload })
      }
    }

    window.addEventListener('keydown', keylistener.current)

    return () => window.removeEventListener('keydown', keylistener.current)
  }, [])

  const handleDismissFilter = (paramToRemove: string) => {
    if (paramToRemove === 'orderBy')
      setCookies(ORDER_BY_COOKIE_KEY, '', {
        path: '/',
        expires: new Date(Date.now() + expirationDurations.year * 1000),
      })

    router.push(`/folders/${id}/?${queryString.stringify({ ...searchedDeckState, [paramToRemove]: undefined })}`)
  }

  const activeFilters = Object.keys(searchedDeckState)
    // @ts-ignore - Seriously TS. The type matches keys from the object
    .map((searchParam: keyof DeckSearchTypeV3) => {
      let searchValue = searchedDeckState[searchParam]
      const searchName = DeckSearchV3HumanName[searchParam]

      if (!searchName) return null
      if (typeof searchParam === 'string' && !searchValue) return null

      // Overrides to more sane values where it makes sense to do so
      // @ts-ignore - searchValue comes in as a string, not a FormatValue
      if (searchParam === 'deckFormat') searchValue = HUMAN_READABLE_FORMAT[Number(searchValue)] || searchValue

      return (
        <div key={searchParam} className={styles.filter}>
          <button onClick={() => handleDismissFilter(searchParam)} className={styles.filterClose}>
            <Icon name="close" />
          </button>{' '}
          <span>
            {searchName}: {`${searchValue}`}
          </span>
        </div>
      )
    })
    .filter(element => element != null)

  return (
    <DragDropProvider>
      <PageWrapper dndPage title={`${name || 'Folder'} - Archidekt`} robots={privateFolder ? 'noindex' : undefined}>
        <Head>
          <meta property="og:url" content={router.asPath} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={owner.avatar} />
          <meta property="og:image:width" content="100" />
          <meta property="og:image:height" content="100" />
          <meta property="og:image:type" content="image/jpeg" />
          <meta name="twitter:label1" content="Subfolders" />
          <meta name="twitter:data1" content={`${subfolders.length}`} />
          <meta name="twitter:label1" content="Decks" />
          <meta name="twitter:data1" content={`${decks.length}`} />
        </Head>

        <CardBanner src={BANNER_IMAGE} height="150px" />

        <div className={styles.container}>
          <div className={styles.topStickyWrapper}>
            <div className={styles.content}>
              <FolderBreadcrumbs />

              <div className={`${styles.controls} ${styles.topSpace}`}>
                <div className={styles.leftControls}>
                  <div className={styles.group}>
                    <form onSubmit={handleFolderNameSearch}>
                      <PhatInput
                        header="Filter deck name"
                        placeholder="Example deck name"
                        className={styles.searchBar}
                        value={searchedName}
                        onChange={setSearchedName}
                        selectOnFocus
                      />
                    </form>

                    <FolderFilterOverlay />
                  </div>
                  <Link href={`/search/decks/?ownerUsername=${owner.username}&fromFolderId=${id}`}>
                    <PhatButton icon="sliders horizontal">Advanced deck search</PhatButton>
                  </Link>
                </div>

                <div className={styles.rightControls}>
                  <PhatDropdown
                    header="View as"
                    value={viewAs}
                    options={[
                      {
                        label: 'List',
                        icon: 'list',
                        onClick: () =>
                          setCookies(FOLDER_VIEW_AS, 'list', {
                            path: '/',
                            expires: new Date(Date.now() + expirationDurations.year * 1000),
                          }),
                        id: 'list',
                      },
                      {
                        label: 'Grid',
                        icon: 'grid layout',
                        onClick: () =>
                          setCookies(FOLDER_VIEW_AS, 'grid', {
                            path: '/',
                            expires: new Date(Date.now() + expirationDurations.year * 1000),
                          }),
                        id: 'grid',
                      },
                    ]}
                  />
                  <div className={styles.group}>
                    <PhatDropdown
                      className={styles.orderByMenu}
                      triggerClassName={styles.orderByMenu}
                      menuClassName={styles.orderByMenu}
                      value={orderBy}
                      header="Order by"
                      options={[
                        {
                          type: 'checkbox',
                          label: 'Sort descending',
                          checked: checkboxDescending,
                          onChange: () => setCheckboxDescending(!checkboxDescending),
                        },
                        { type: 'spacer' },
                        {
                          icon: 'edit outline',
                          label: 'Updated at',
                          onClick: () => handleSetOrderBy('updatedAt'),
                          id: 'updatedAt',
                        },
                        {
                          icon: 'calendar alternate outline',
                          label: 'Created at',
                          onClick: () => handleSetOrderBy('createdAt'),
                          id: 'createdAt',
                        },
                        { type: 'spacer' },
                        {
                          icon: 'sort alphabet ascending',
                          label: 'Name',
                          onClick: () => handleSetOrderBy('name'),
                          id: 'name',
                        },
                        {
                          icon: 'circle outline',
                          label: 'Deck format / bracket',
                          onClick: () => handleSetOrderBy('format'),
                          id: 'format',
                        },
                        {
                          icon: 'adjust',
                          label: 'Colors',
                          onClick: () => handleSetOrderBy('colors'),
                          id: 'colors',
                        },
                        { icon: 'lock', label: 'Private', onClick: () => handleSetOrderBy('private'), id: 'private' },
                        {
                          icon: 'eye slash outline',
                          label: 'Unlisted',
                          onClick: () => handleSetOrderBy('unlisted'),
                          id: 'unlisted',
                        },
                        {
                          icon: 'question circle outline',
                          label: 'Theorycrafted',
                          onClick: () => handleSetOrderBy('theorycrafted'),
                          id: 'theorycrafted',
                        },
                      ]}
                    />

                    <ArchidektDropdown
                      menuClassName={styles.extrasMenu}
                      options={[
                        {
                          icon: 'add circle',
                          label: 'Create new folder',
                          hidden: !isFolderOwner,
                          onClick: () => setFolderState({ newFolderModalOpen: true }),
                        },
                        {
                          icon: 'add circle',
                          label: 'Create new deck',
                          hidden: !isFolderOwner,
                          onClick: () => router.push(`/new-deck/?parentFolderId=${id}`),
                        },
                        { type: 'spacer', hidden: !isFolderOwner },
                        {
                          icon: 'hand pointer outline',
                          label: 'Select all',
                          onClick: () => handleSelectAll(),
                          keybind: 'Ctrl+A',
                        },
                      ]}>
                      <PhatButton icon="ellipsis vertical">Options</PhatButton>
                    </ArchidektDropdown>
                  </div>
                </div>
              </div>
              <div className={styles.totalResults}>Total decks: {totalResults > 999 ? '1000+' : totalResults}</div>

              <div className={styles.filters}>
                {activeFilters}
                {activeFilters.length > 1 && (
                  <Link href={`/folders/${id}`} className={styles.clearAllFiltersButton}>
                    Clear all filters
                  </Link>
                )}
              </div>

              {viewAs === 'list' && <FolderColumnControls />}
            </div>
          </div>

          <div className={styles.sideScrollingContent}>
            {viewAs === 'list' && <FolderTable />}
            {viewAs === 'grid' && <FolderGrid />}
          </div>

          <div className={styles.stickyWrapper}>
            <div className={styles.pageControls}>
              <div>
                <Link
                  tabIndex={page !== 1 ? undefined : -1}
                  href={page !== 1 ? buildUrlForPage(id, activeFilters, page - 1) : '/'}>
                  <PhatButton tabIndex={-1} disabled={!router.query.page || page === 1}>
                    Previous page
                  </PhatButton>
                </Link>

                <Link
                  tabIndex={nextPage ? undefined : -1}
                  href={nextPage ? buildUrlForPage(id, activeFilters, page + 1) : '/'}>
                  <PhatButton tabIndex={-1} disabled={!nextPage}>
                    Next page
                  </PhatButton>
                </Link>
              </div>

              <div>
                <PhatInput
                  header="Page number"
                  placeholder="8"
                  value={exactPageNumber}
                  onChange={setExactPageNumber}
                  className={styles.exactPageNumber}
                  type="number"
                />

                <Link href={buildUrlForPage(id, activeFilters, exactPageNumber)}>
                  <PhatButton icon="arrow alternate circle right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      <FolderDragLayer />
      <MultiSelectControls />
      <NewFolderModal />
    </DragDropProvider>
  )
}

export default FolderPage

export const getServerSideProps: GetServerSideProps<ReduxServerProps> = async context => {
  try {
    const cachedOrderBy = CookieService.get(ORDER_BY_COOKIE_KEY, context.req?.cookies)
    const orderBy = context.query.orderBy || cachedOrderBy || '-updatedAt'

    if (context.query.orderBy)
      CookieService.setServerCookie({ [ORDER_BY_COOKIE_KEY]: orderBy }, context, expirationDurations.year)

    const folderId = `${context.params?.folderId}`

    const queryParmas: Record<string, any> = { ...context.query, orderBy }

    delete queryParmas.folderId // Folder Id is part of the router, not an actual query param

    const query = queryString.stringify(queryParmas)

    const rootFolder = await new FolderService(context).details(folderId, query)
    const cachedReduxState = generateInitialFolderState(context.req.cookies)

    return {
      props: {
        redux: { folders: { ...cachedReduxState, rootFolder, selectedDecks: {}, selectedSubfolders: {} } },
      },
    }
  } catch (err) {
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}

const buildUrlForPage = (
  id: number | string,
  existingParams: Record<string, any>,
  pageNumber: string | number,
): string => {
  const query = queryString.stringify({ ...existingParams, page: Number(pageNumber) || 1 })

  return `/folders/${id}/?${query}`
}
